/**
 * How the UI names, pictures and shows a file.
 *
 * Three axes, deliberately separate:
 *
 * - **Label** (`getFileExtensionLabel`) — the plain uppercase extension a person reads in
 *   a TYPE column ("PDF", "XLSX"). Never a MIME string: `application/vnd.openxmlformats-
 *   officedocument.spreadsheetml.sheet` is a transport detail, not a file type, and it is
 *   what the retired welcome screen used to render.
 * - **Kind** (`getFileKind`) — the presentation bucket that picks an icon. The coarse
 *   document/image/audio/video decision is NOT made here: it belongs to
 *   `classifyArtifactFile` in `@repo/shared`, the same call the upload validator and the
 *   backend processing router make. Disagreeing with it is how a `chart.png` that arrived
 *   as `application/octet-stream` ends up drawn as a document while the pipeline treats it
 *   as an image. This module only refines the `"document"` result by extension.
 * - **Preview kind** (`getPreviewKind`) — which body, if any, can render the bytes in the
 *   page instead of downloading them. Narrower than `Kind` and answering a different
 *   question: a `.pptx` has a confident icon and no preview at all.
 */

import { classifyArtifactFile } from "@repo/shared";
import {
  FileArchive,
  FileAudio,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Image as ImageIcon,
  Link as LinkIcon,
  type LucideIcon,
  Presentation,
} from "lucide-react";

export type FileKind =
  | "pdf"
  | "document"
  | "presentation"
  | "spreadsheet"
  | "image"
  | "audio"
  | "video"
  | "archive"
  | "notes"
  | "link";

/** The four bodies a file can be shown with in the app, rather than handed over as bytes. */
export type DocumentPreviewKind = "image" | "pdf" | "markdown" | "text";

const PRESENTATION_EXTENSIONS = new Set(["key", "odp", "ppt", "pptx"]);
const SPREADSHEET_EXTENSIONS = new Set(["csv", "numbers", "ods", "tsv", "xls", "xlsm", "xlsx"]);

/**
 * The image formats a browser draws in an `<img>`. Narrower than the shared classifier's image
 * set on purpose: TIFF and HEIC are images the pipeline can describe, but an `<img>` of one is
 * a broken picture, so they are handed over rather than previewed.
 */
const BROWSER_IMAGE_EXTENSIONS = new Set([
  "avif",
  "bmp",
  "gif",
  "jpeg",
  "jpg",
  "png",
  "svg",
  "webp",
]);
const BROWSER_IMAGE_MEDIA_TYPES = new Set([
  "image/avif",
  "image/bmp",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/svg+xml",
  "image/webp",
]);

const PDF_EXTENSION = "pdf";
const PDF_MEDIA_TYPE = "application/pdf";
const MARKDOWN_EXTENSIONS = new Set(["markdown", "md"]);
const MARKDOWN_MEDIA_TYPES = new Set(["text/markdown"]);
const TEXT_EXTENSIONS = new Set(["srt", "text", "txt", "vtt"]);
const TEXT_MEDIA_TYPES = new Set(["application/x-subrip", "text/plain", "text/vtt"]);

const KIND_ICONS: Record<FileKind, LucideIcon> = {
  pdf: FileText,
  document: FileText,
  presentation: Presentation,
  spreadsheet: FileSpreadsheet,
  image: ImageIcon,
  audio: FileAudio,
  video: FileVideo,
  archive: FileArchive,
  notes: LinkIcon,
  link: LinkIcon,
};

/** Lowercased extension without the dot, or `""` when the name carries none. */
function fileExtension(name: string): string {
  const basename = name.trim().toLowerCase().split(/[\\/]/).pop() ?? "";
  const dot = basename.lastIndexOf(".");
  // `dot > 0` on purpose: `.gitignore` is a dotfile, not a file of type "gitignore".
  return dot > 0 ? basename.slice(dot + 1) : "";
}

/** The bare media type, lowercased and without parameters, or `""` when there is none. */
function mediaType(mimeType?: string | null): string {
  return mimeType?.split(";")[0]?.trim().toLowerCase() ?? "";
}

/**
 * The uppercase extension to show in a TYPE column, or `null` when the name has none.
 *
 * @example
 * getFileExtensionLabel("Q3 review.pptx") // "PPTX"
 * getFileExtensionLabel("archive.tar.gz") // "GZ"
 * getFileExtensionLabel("README")         // null
 * getFileExtensionLabel(".gitignore")     // null
 */
export function getFileExtensionLabel(name: string): string | null {
  const extension = fileExtension(name);
  return extension.length > 0 ? extension.toUpperCase() : null;
}

/**
 * The presentation bucket for a file, for picking an icon.
 *
 * Delegates the document/image/audio/video/archive split to the shared `classifyArtifactFile`,
 * then refines a `"document"` by extension (and by MIME as a secondary signal, since
 * Office formats have long, easily-truncated content types). Never returns `"notes"` or
 * `"link"` — those are kinds a caller constructs for non-file rows.
 */
export function getFileKind(name: string, mimeType?: string): FileKind {
  const fileClass = classifyArtifactFile(name, mimeType);
  if (fileClass !== "document") return fileClass;

  const extension = fileExtension(name);
  const mime = mediaType(mimeType);

  if (PRESENTATION_EXTENSIONS.has(extension) || mime.includes("presentation")) {
    return "presentation";
  }
  if (
    SPREADSHEET_EXTENSIONS.has(extension) ||
    mime.includes("spreadsheet") ||
    mime.includes("csv")
  ) {
    return "spreadsheet";
  }
  if (extension === "pdf" || mime.includes("pdf")) return "pdf";
  return "document";
}

/**
 * How the app can show this file itself, or `null` when it can only hand the bytes over.
 *
 * Whether a file *is* an image is the shared classifier's verdict, never a second MIME test
 * here, so a `chart.png` that arrived as `application/octet-stream` is still the image the
 * pipeline treats it as; whether the browser can *draw* it is this module's own question, asked
 * of the extension or the exact media type. The other three kinds are decided the same way:
 * unlike the icon buckets, a wrong guess here renders bytes into a body that cannot show them.
 */
export function getPreviewKind(name: string, mimeType?: string | null): DocumentPreviewKind | null {
  const extension = fileExtension(name);
  const mime = mediaType(mimeType);

  if (classifyArtifactFile(name, mimeType ?? undefined) === "image") {
    return BROWSER_IMAGE_EXTENSIONS.has(extension) || BROWSER_IMAGE_MEDIA_TYPES.has(mime)
      ? "image"
      : null;
  }

  if (extension === PDF_EXTENSION || mime === PDF_MEDIA_TYPE) return "pdf";
  if (MARKDOWN_EXTENSIONS.has(extension) || MARKDOWN_MEDIA_TYPES.has(mime)) return "markdown";
  if (TEXT_EXTENSIONS.has(extension) || TEXT_MEDIA_TYPES.has(mime)) return "text";
  return null;
}

/** The Lucide icon for a file kind. Total over `FileKind` — there is no fallback branch. */
export function getFileTypeIcon(kind: FileKind): LucideIcon {
  return KIND_ICONS[kind];
}
